import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import * as types from '@Constants/actionTypes';
import Notification from '@Common/Notification';
import DatePicker from '@Common/DatePicker/DatePicker';
import ToggleV6 from '@Components/Common/ToggleV6/ToggleV6';
import { actions as projectActions } from '@Actions/projectActions';
import { useGetZeroDateDetails } from '@Apis/projectLocation';
import { ProjectLocation } from '@Types/ProjectLocation';
import { formatDate, formatCurrentDate, parseDisplayDate } from '@Helpers/dateTime';

import './ChangeZeroDateDrawerModal.scss';

interface Props {
	projectActions: typeof projectActions;
	selectedProjectLocations: ProjectLocation[];
	projectKey: string;
	onComplete: (skippedSomeLocations: boolean) => void;
	onCancel: () => void;
	onDataChange: (data: {
		selectedZeroDate: string;
		epicDates: { projectEpicLabelId: string; value: Date }[];
		selectedRadioOption: string;
		isFormValid: boolean;
		updateLocationOpeningDate: boolean;
		locationOpeningDate: string;
	}) => void;
}

const ChangeZeroDateDrawerModal: React.FC<Props> = ({
	projectActions,
	selectedProjectLocations,
	projectKey,
	onComplete,
	onCancel,
	onDataChange,
}) => {
	const [selectedZeroDate, setSelectedZeroDate] = useState<string>('');
	const [isFormValid, setIsFormValid] = useState<boolean>(true);
	const [projectEpicLabels, setProjectEpicLabels] = useState<{ id: string; name: string; value?: string }[]>([]);
	const [epicDates, setEpicDates] = useState<{ projectEpicLabelId: string; value: Date }[]>([]);
	const [selectedRadioOption, setSelectedRadioOption] = useState<string>('1');

	// Opening date toggle (single-location only)
	const [updateLocationOpeningDate, setUpdateLocationOpeningDate] = useState<boolean>(false);
	const [selectedOpeningDate, setSelectedOpeningDate] = useState<string>('');

	const isSingleLocation = selectedProjectLocations.length === 1;
	const singleLocationId = isSingleLocation ? selectedProjectLocations[0]?.id : undefined;

	// Current dates loaded for a single selected location
	const { data: zeroDateDetails } = useGetZeroDateDetails(
		{ projectLocationId: singleLocationId ?? 0, projectKey },
		{ enabled: !!singleLocationId }
	);

	// Captions are gated on the formatted value, not the raw one: an unset or unparseable
	// date must hide the caption entirely rather than render an empty 'Current Date:'.
	const currentZeroDate = formatCurrentDate(zeroDateDetails?.zeroDate);
	const currentOpeningDate = formatCurrentDate(zeroDateDetails?.openingDate);
	const currentEpicDates = useMemo(() => {
		const epicMap: Record<string, string> = {};
		(zeroDateDetails?.epicDates ?? []).forEach((epic) => {
			const formatted = formatCurrentDate(epic.value);
			if (formatted) epicMap[`${epic.projectEpicLabelId}`] = formatted;
		});
		return epicMap;
	}, [zeroDateDetails]);

	useEffect(() => {
		if (!isSingleLocation) {
			setUpdateLocationOpeningDate(false);
			setSelectedOpeningDate('');
		}
	}, [isSingleLocation]);

	useEffect(() => {
		const fetchProject = async () => {
			try {
				const result: any = await projectActions.getProject(projectKey);
				if (result.type === types.GET_PROJECT_SUCCESS) {
					setProjectEpicLabels(result.payload.projectEpicLabels);
				} else {
					Notification('error', 'An Error Occurred', result.payload.message);
				}
			} catch (error) {
				Notification('error', 'An Error Occurred', String(error));
			}
		};
		fetchProject();
	}, [projectActions, projectKey]);

	// Notify parent when data changes
	useEffect(() => {
		onDataChange({
			selectedZeroDate,
			epicDates,
			selectedRadioOption,
			isFormValid,
			updateLocationOpeningDate,
			locationOpeningDate: selectedOpeningDate,
		});
	}, [selectedZeroDate, epicDates, selectedRadioOption, isFormValid, updateLocationOpeningDate, selectedOpeningDate]);

	// Validate form whenever relevant fields change
	useEffect(() => {
		const isValid = selectedZeroDate.trim() !== '' && selectedRadioOption !== '';
		setIsFormValid(isValid);
	}, [selectedZeroDate, selectedRadioOption]);

	const onChangeZeroDate = (date: string) => {
		setSelectedZeroDate(date);
	};

	const onChangeEpicDate = (calendarDate: string, epicLabel: string) => {
		const date = parseDisplayDate(calendarDate);
		setEpicDates((prevEpicDates) => {
			const labelIndex = prevEpicDates.findIndex((label) => label.projectEpicLabelId === epicLabel);
			if (labelIndex !== -1) {
				return prevEpicDates.map((label, index) => (index === labelIndex ? { ...label, value: date } : label));
			} else {
				return [...prevEpicDates, { projectEpicLabelId: epicLabel, value: date }];
			}
		});
	};

	const onChangeRadioOption = (value: string) => {
		setSelectedRadioOption(value);
	};

	const onToggleUpdateOpeningDate = (_name: string, checked: boolean) => {
		setUpdateLocationOpeningDate(checked);
		if (!checked) {
			setSelectedOpeningDate('');
		}
	};

	const onChangeOpeningDate = (date: string) => {
		setSelectedOpeningDate(date);
	};

	return (
		<div className='task-details-content-zero-date-modal col-12' id='accordion'>
			<div className='content-title d-flex align-items-center justify-content-between'>
				Date Settings
			</div>
			<form noValidate className={isFormValid ? '' : 'was-validated'}>
				<div className='row me-3'>
					<div className='col-6'>
						<div className='date-picker-container'>
							<DatePicker
								value={selectedZeroDate}
								name='zeroDate'
								label='Zero Date'
								onChange={(date) => onChangeZeroDate(date)}
								isRequired={true}
								errorMessage='Zero Date Required'
							/>
							{isSingleLocation && currentZeroDate && (
								<div className='current-date-caption' data-testid='current-zero-date'>
									Current Date: {currentZeroDate}
								</div>
							)}
						</div>
						{
							projectEpicLabels.map((epicLabel, index) => {
								const epicDateObj = epicDates.find(
									d => d.projectEpicLabelId === epicLabel.id
								);

								const value = epicDateObj?.value && !Number.isNaN(epicDateObj.value.getTime())
									? formatDate(epicDateObj.value)
									: '';
								const currentEpicValue = currentEpicDates[epicLabel.id];

								return (
									<div key={index} className='date-picker-container'>
										<DatePicker
											value={value}
											name={`${epicLabel.id}`}
											label={epicLabel.name}
											onChange={(date) => onChangeEpicDate(date, epicLabel.id)}
										/>
										{isSingleLocation && currentEpicValue && (
											<div className='current-date-caption' data-testid={`current-epic-date-${epicLabel.id}`}>
												Current Date: {currentEpicValue}
											</div>
										)}
									</div>
								);
							})
						}
						{isSingleLocation && (
							<div className='opening-date-toggle'>
								<ToggleV6
									name='updateLocationOpeningDate'
									text='Change Location Opening Date'
									checked={updateLocationOpeningDate}
									onChange={onToggleUpdateOpeningDate}
								/>
								{updateLocationOpeningDate && (
									<div className='date-picker-container opening-date-picker'>
										<DatePicker
											value={selectedOpeningDate}
											name='openingDate'
											label='Opening Date'
											onChange={(date) => onChangeOpeningDate(date)}
										/>
										{currentOpeningDate && (
											<div className='current-date-caption' data-testid='current-opening-date'>
												Current Date: {currentOpeningDate}
											</div>
										)}
									</div>
								)}
							</div>
						)}
					</div>
					<div className='col-6'>
						<div className='change-date-options'>
							<label className='title mb-2'>How should task dates be updated?</label>
							<div className='form-check'>
								<input
									className='form-check-input'
									type='radio'
									name='radioOptions'
									id='radio1'
									value='1'
									checked={selectedRadioOption === '1'}
									onChange={(e) => onChangeRadioOption(e.target.value)}
								/>
								<label className='form-check-label' htmlFor='radio1'>
									<b>Simple Shift.</b> Shift all location tasks by the same number of days; the difference between the old and new Zero Date.
								</label>
							</div>
							<div className='form-check'>
								<input
									className='form-check-input'
									type='radio'
									name='radioOptions'
									id='radio2'
									value='2'
									checked={selectedRadioOption === '2'}
									onChange={(e) => onChangeRadioOption(e.target.value)}
								/>
								<label className='form-check-label' htmlFor='radio2'>
									<b>Keep Custom Dates.</b> Shift all location tasks by the same number of days, except for tasks that have been customized.
								</label>
							</div>
							<div className='form-check'>
								<input
									className='form-check-input'
									type='radio'
									name='radioOptions'
									id='radio3'
									value='3'
									checked={selectedRadioOption === '3'}
									onChange={(e) => onChangeRadioOption(e.target.value)}
								/>
								<label className='form-check-label' htmlFor='radio3'>
									<b>Full Reset.</b> Reset all location tasks dates to match the baseline.
								</label>
							</div>
							<p><a href='https://ehsolution.zendesk.com/hc/en-us/articles/28609102700699-How-to-Update-a-Location-s-Zero-Date' target='_blank'>How does this work?</a></p>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
};

export { ChangeZeroDateDrawerModal as UnconnectedChangeZeroDateDrawerModal };

const mapDispatchToProps = (dispatch: Dispatch) => {
	return {
		projectActions: bindActionCreators(projectActions, dispatch),
	};
};

export default connect(null, mapDispatchToProps)(ChangeZeroDateDrawerModal);
